import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getDashboardData } from '../../api/DashboardAPI';
import { AuthContext } from '../AuthContext';
import AppBreadCrumb from '../common/AppBreadCrumb';
import { Can } from '../../hooks/usePermissions';
import {Bar} from 'react-chartjs-2';

// --- NEW: A helper component for progress bars ---
const MilestoneProgressBar = ({ label, value, goal }) => {
    const progress = goal > 0 ? (value / goal) * 100 : 0;
    return (
        <div>
            <div className="d-flex justify-content-between">
                <span>{label}</span>
                <strong>{value} / {goal}</strong>
            </div>
            <div className="progress" style={{ height: '10px' }}>
                <div className="progress-bar bg-success" role="progressbar" style={{ width: `${progress}%` }} aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100"></div>
            </div>
        </div>
    );
};


const ClubPortalDashboard = () => {
    const { user } = useContext(AuthContext);
    const [dashboardData, setDashboardData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        getDashboardData((response) => {
            if (response) {
                setDashboardData(response);
            }
            setIsLoading(false);
        });
    }, []);


     // --- UPDATED: This now creates two datasets for a grouped bar chart ---
    const spendingChartData = useMemo(() => {
        if (!dashboardData?.yearwise_spending) {
            return null;
        }
        
        const data = dashboardData.yearwise_spending;
        const labels = data.map(item => item.year);

        return {
            labels,
            datasets: [
                {
                    label: 'Spent on Self (₹)',
                    data: data.map(item => item.total_self_spent),
                    backgroundColor: 'rgb(15, 98, 106)',
                },
                {
                    label: 'Spent on Guests (₹)',
                    data: data.map(item => item.total_guest_spent),
                    backgroundColor: 'rgba(156, 106, 55, 1)',
                }
            ],
        };
    }, [dashboardData]);

    // --- UPDATED: Chart options with a new title ---
    const chartOptions = {
        responsive: true,
        plugins: {
            legend: { position: 'top' },
            title: {
                display: true,
                text: 'My Spending History: Self vs. Guests',
            },
        },
        scales: {
            y: { beginAtZero: true },
            x: { stacked: false }, // Ensure bars are side-by-side
        },
    };


     // --- NEW: Memoized values for the new widgets ---
    const milestones = useMemo(() => {
        if (!dashboardData?.personal_stats) return null;
        const { total_runs, total_wickets, total_games } = dashboardData.personal_stats;
        return {
            runs: { value: total_runs, goal: Math.ceil(total_runs / 500) * 500 || 500 },
            wickets: { value: total_wickets, goal: Math.ceil(total_wickets / 50) * 50 || 50 },
            games: { value: total_games, goal: Math.ceil(total_games / 100) * 100 || 100 },
        };
    }, [dashboardData]);

    const bests = useMemo(() => {
        if (!dashboardData?.personal_bests) return {};
        const { highest_score, best_bowling } = dashboardData.personal_bests;
        return {
            hs: highest_score ? `${highest_score.runs_scored}${highest_score.dismissal_type === 'not_out' ? '*' : ''}` : 'N/A',
            bb: best_bowling ? `${best_bowling.wickets_taken}/${best_bowling.runs_conceded}` : 'N/A',
        };
    }, [dashboardData]);

    if (isLoading) {
        return <div className="text-center p-5"><div className="spinner-border"></div></div>;
    }

    if (!dashboardData) {
        return <div className="text-center p-5">Could not load dashboard data.</div>;
    }

    const isClubAdmin = user?.roles?.some(role => role.name === 'club-admin');

    const formatBirthday = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    };

    return (
        <main>
            <div className="container-fluid">
                <AppBreadCrumb title={`Welcome, ${user?.name}!`} />

                <div className="row">
                    <div className="col-lg-4 col-md-6 mb-4">
                        <div className="card h-100 bg-primary text-white shadow">
                            <div className="card-body">
                                <h5 className="card-title">My Wallet Balance</h5>
                                <h2 className="display-5 fw-bold text-white">₹{Number(dashboardData.wallet_balance).toFixed(2)}</h2>
                                <Link to="/club_portal/my-wallet" className="btn btn-light btn-sm mt-2">View My Wallet <i className="fas fa-arrow-right ms-1"></i></Link>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-8 col-md-6 mb-4">
                        <div className="card h-100 shadow-sm">
                            <div className="card-header"><h5>My Career Stats</h5></div>
                            <div className="card-body d-flex justify-content-around align-items-center text-center">
                                <div><h4 className="fw-bold mb-0">{dashboardData.personal_stats.total_runs || 0}</h4><p className="text-muted mb-0">Runs</p></div>
                                <div className="stat-divider"></div>
                                <div><h4 className="fw-bold mb-0">{dashboardData.personal_stats.total_wickets || 0}</h4><p className="text-muted mb-0">Wickets</p></div>
                                <div className="stat-divider"></div>
                                <div><h4 className="fw-bold mb-0">{dashboardData.personal_stats.total_innings || 0}</h4><p className="text-muted mb-0">Innings</p></div>
                            </div>
                            <div className="card-footer text-end">
                                <Link to={`/club_portal/members/${user?.id}`} className="btn btn-outline-primary btn-sm">View Full Stats</Link>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-lg-6 mb-4">
                        <div className="card h-100 shadow-sm">
                            <div className="card-header"><h5><i className="fas fa-trophy me-2 text-warning"></i>Upcoming Games</h5></div>
                            <div className="card-body">
                                {dashboardData.upcoming_games.length > 0 ? (
                                    <ul className="list-group list-group-flush">
                                        {dashboardData.upcoming_games.map(game => (
                                            <li key={game.id} className="list-group-item d-flex justify-content-between align-items-center">
                                                <div>
                                                    <strong>{game.teams.map(t => t.name).join(' vs ')}</strong>
                                                    <br /><small className="text-muted">{game.venue.name}</small>
                                                </div>
                                                <span className="badge bg-primary rounded-pill">{new Date(game.game_date).toLocaleDateString()}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : <p>No upcoming games scheduled.</p>}
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6 mb-4">
                        <div className="card h-100 shadow-sm">
                            <div className="card-header"><h5><i className="fas fa-calendar-alt me-2 text-info"></i>Upcoming Bookings</h5></div>
                            <div className="card-body">
                                {dashboardData.upcoming_bookings.length > 0 ? (
                                    <ul className="list-group list-group-flush">
                                        {dashboardData.upcoming_bookings.map(slot => (
                                            <li key={slot.id} className="list-group-item d-flex justify-content-between align-items-center">
                                                <div>
                                                    <strong>{slot.turf_booking.venue.name}</strong>
                                                    <br /><small className="text-muted">{slot.start_time} - {slot.end_time}</small>
                                                </div>
                                                <span className="badge bg-info rounded-pill text-dark">{slot.slot_date}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : <p>No upcoming turf bookings.</p>}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-lg-4 mb-4">
                        <div className="card h-100 shadow-sm">
                            <div className="card-header"><h5><i className="fas fa-birthday-cake me-2 text-danger"></i>Upcoming Birthdays</h5></div>
                            <div className="card-body">
                                {dashboardData.upcoming_birthdays?.length > 0 ? (
                                    <ul className="list-group list-group-flush">
                                        {dashboardData.upcoming_birthdays.map(member => (
                                            <li key={member.name} className="list-group-item d-flex justify-content-between align-items-center">
                                                {member.name}
                                                <span className="badge bg-danger rounded-pill">{formatBirthday(member.date_of_birth)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : <p>No upcoming birthdays found.</p>}
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-4 mb-4">
                        <div className="card h-100 shadow-sm">
                            <div className="card-header"><h5><i className="fas fa-award me-2 text-primary"></i>Recent Awards</h5></div>
                            <div className="card-body">
                                {dashboardData.recent_awards?.length > 0 ? (
                                    <ul className="list-group list-group-flush">
                                        {dashboardData.recent_awards.map(award => (
                                            <li key={award.id} className="list-group-item">
                                                <strong>{award.award_type}</strong>
                                                <br />
                                                <small className="text-success">{award.user.name}</small>
                                                <small className="text-muted float-end">{new Date(award.start_date).getFullYear()}</small>
                                            </li>
                                        ))}
                                    </ul>
                                ) : <p>No recent awards have been given.</p>}
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-4 mb-4">
                        <div className="card h-100 shadow-sm">
                            <div className="card-header"><h5><i className="fas fa-glass-cheers me-2 text-success"></i>Upcoming Events</h5></div>
                            <div className="card-body">
                                {dashboardData.upcoming_events?.length > 0 ? (
                                    <ul className="list-group list-group-flush">
                                        {dashboardData.upcoming_events.map(event => (
                                            <li key={event.id} className="list-group-item d-flex justify-content-between align-items-center">
                                                {event.name}
                                                <span className="badge bg-success rounded-pill">{new Date(event.start_time).toLocaleDateString()}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : <p>No upcoming club events scheduled.</p>}
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* --- NEW: PERSONAL STATISTICS ROW --- */}
                <div className="row">
                    {/* Milestone Tracker */}
                    <div className="col-lg-4 mb-4">
                        <div className="card h-100 shadow-sm">
                            <div className="card-header"><h5><i className="fas fa-flag-checkered me-2 text-primary"></i>Milestone Tracker</h5></div>
                            <div className="card-body">
                                {milestones && (
                                    <div className="d-flex flex-column gap-3">
                                        <MilestoneProgressBar label="Next 500 Runs" value={milestones.runs.value} goal={milestones.runs.goal} />
                                        <MilestoneProgressBar label="Next 50 Wickets" value={milestones.wickets.value} goal={milestones.wickets.goal} />
                                        <MilestoneProgressBar label="Next 100 Games" value={milestones.games.value} goal={milestones.games.goal} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Recent Form & Personal Bests */}
                    <div className="col-lg-4 mb-4">
                        <div className="card h-100 shadow-sm">
                            <div className="card-header"><h5><i className="fas fa-chart-line me-2 text-success"></i>Recent Form (Last 60 Days)</h5></div>
                            <div className="card-body d-flex justify-content-around align-items-center text-center">
                                <div><h3 className="fw-bold mb-0">{dashboardData.recent_form.runs}</h3><p className="text-muted mb-0">Runs</p></div>
                                <div className="stat-divider"></div>
                                <div><h3 className="fw-bold mb-0">{dashboardData.recent_form.wickets}</h3><p className="text-muted mb-0">Wickets</p></div>
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-4 mb-4">
                        <div className="card h-100 shadow-sm">
                            <div className="card-header"><h5><i className="fas fa-star me-2 text-warning"></i>Personal Bests</h5></div>
                            <div className="card-body d-flex justify-content-around align-items-center text-center">
                                <div><h3 className="fw-bold mb-0">{bests.hs}</h3><p className="text-muted mb-0">Highest Score</p></div>
                                <div className="stat-divider"></div>
                                <div><h3 className="fw-bold mb-0">{bests.bb}</h3><p className="text-muted mb-0">Best Bowling</p></div>
                            </div>
                        </div>
                    </div>
                </div>


                 {/* --- NEW: Year-wise Spending Chart Widget --- */}
                 <div className="row">
                    <div className="col-lg-6 mb-4">
                        <div className="card shadow-sm">
                            <div className="card-header">
                                <h5>My Spending History</h5>
                            </div>
                            <div className="card-body">
                                {spendingChartData && spendingChartData.labels.length > 0 ? (
                                    <Bar options={chartOptions} data={spendingChartData} />
                                ) : (
                                    <p>No spending data available to display.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>


                <Can any={['create-user','create-turf-booking','create-game']}>
                    <div className="row">
                        <div className="col-12">
                            <div className="card shadow-sm">
                                <div className="card-header"><h5>Admin Actions</h5></div>
                                <div className="card-body">
                                    <Can any={['generate-contributions']}>
                                        {dashboardData.admin_data.pending_contributions_count > 0 ? (
                                            <div className="alert alert-warning">
                                                You have <strong>{dashboardData.admin_data.pending_contributions_count}</strong> past booking(s) that need contributions to be generated.
                                                <Link to="/club_portal/financials/contributions" className="alert-link ms-2">Manage Now</Link>
                                            </div>
                                        ) : (
                                            <p>All contributions are up to date.</p>
                                        )}
                                    </Can>
                                    <hr />
                                    <Can any={['create-user']}>
                                        <Link to="/club_portal/users/create" className="btn btn-outline-primary me-2 mt-2">Add New Member</Link>
                                    </Can>
                                    <Can any={['create-turf-booking']}>
                                        <Link to="/club_portal/bookings/create" className="btn btn-outline-primary me-2 mt-2">New Turf Booking</Link>
                                    </Can>
                                    <Can any={['create-game']}>

                                        <Link to="/club_portal/games/create" className="btn btn-outline-primary mt-2">Schedule New Game</Link>
                                    </Can>
                                </div>
                            </div>
                        </div>
                    </div>
                </Can>

            </div>
            <style>{`.stat-divider { width: 1px; background-color: #e0e0e0; }`}</style>
        </main>
    );
};

export default ClubPortalDashboard;